import type {
  AftersalesAnalytics,
  AftersalesPage,
  ApiPayload,
  BestsellerSnapshotPage,
  OperationsCapability,
  OrderAnalytics,
  TiktokRankCapabilities,
  TiktokRankSnapshot,
} from './model';

import { requestClient } from '#/api/request';

const base = '/merchant/tiktok-operations';
const rankBase = '/merchant/tiktok-ranks';

export function getTiktokRankCapabilitiesApi() {
  return requestClient.post<TiktokRankCapabilities>(`${rankBase}/capabilities`);
}

export function getLatestTiktokRankSnapshotApi(data: Record<string, any>) {
  return requestClient.post<null | TiktokRankSnapshot>(
    `${rankBase}/latest`,
    data,
    { timeout: 60_000 },
  );
}

export function prewarmTiktokDataApi() {
  return requestClient.post<Record<string, any>>(`${base}/prewarm`);
}

export function getOperationsCapabilityApi(shopId: number) {
  return requestClient.post<OperationsCapability>(`${base}/capability`, {
    shopId,
  });
}

export function searchAftersalesApi(data: Record<string, any>) {
  return requestClient.post<AftersalesPage>(`${base}/aftersales/search`, data, {
    timeout: 60_000,
  });
}

export function syncAftersalesApi(data: { shopId?: number }) {
  return requestClient.post<ApiPayload>(`${base}/aftersales/sync`, data);
}

export function getAftersalesDecisionEligibilityApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(
    `${base}/aftersales/decision-eligibility`,
    data,
  );
}

export function approveAftersalesApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/aftersales/approve`, data);
}

export function rejectAftersalesApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/aftersales/reject`, data);
}

export function getBestsellersApi(data: {
  categoryId?: string;
  rankingType: 'CREATORS' | 'LIVES' | 'PRODUCTS' | 'VIDEOS';
  timeSlot: '1D' | '7D' | '30D';
}) {
  return requestClient.post<BestsellerSnapshotPage>(
    `${base}/bestsellers`,
    data,
  );
}

export function getShopAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/analytics/shop`, data, {
    timeout: 60_000,
  });
}

export function getShopHourlyAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/analytics/shop/hourly`, data, {
    timeout: 60_000,
  });
}

export function getProductAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/analytics/products`, data, {
    timeout: 60_000,
  });
}

export function getSkuAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/analytics/skus`, data, {
    timeout: 60_000,
  });
}

export function getProductAnalyticsPageApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/analytics/products/page`,
    data,
    { timeout: 120_000 },
  );
}

export function getProductTrafficAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/analytics/product-traffic`,
    data,
    { timeout: 120_000 },
  );
}

export function getProductAnalyticsDetailApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/analytics/products/detail`,
    data,
    { timeout: 120_000 },
  );
}

export function createProductOperationTaskApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-tasks/create`,
    data,
  );
}

export function getProductOperationTasksApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-tasks/list`,
    data,
  );
}

export function getProductOperationTaskDetailApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-tasks/detail`,
    data,
  );
}

export function getOrderAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<OrderAnalytics>(`${base}/analytics/orders`, data);
}

export function getAftersalesAnalyticsApi(data: Record<string, any>) {
  return requestClient.post<AftersalesAnalytics>(
    `${base}/analytics/aftersales`,
    data,
    { timeout: 90_000 },
  );
}

export function searchProductsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/products/search`, data, {
    timeout: 60_000,
  });
}

export function getStoreProductDetailApi(data: {
  productId: string;
  shopId: number;
}) {
  return requestClient.post<ApiPayload>(`${base}/products/detail`, data, {
    timeout: 60_000,
  });
}

export function searchProductManagementApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-management/search`,
    data,
  );
}

export interface ProductManagementCategory {
  categoryId: string;
  categoryIdLevel1?: string;
  categoryIdLevel2?: string;
  categoryIdLevel3?: string;
  categoryNameLevel1?: string;
  categoryNameLevel2?: string;
  categoryNameLevel3?: string;
  leaf?: boolean;
}

export function getProductManagementCategoriesApi(data: {
  level?: 1 | 2 | 3;
  parentCategoryId?: string;
}) {
  return requestClient.post<ProductManagementCategory[]>(
    `${base}/product-management/categories`,
    data,
  );
}

export function getProductManagementDetailApi(data: {
  productId: string;
  shopId: number;
}) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-management/detail`,
    data,
  );
}

export function cacheProductManagementImagesApi(data: { imageIds: number[] }) {
  return requestClient.post<{
    images: Array<{
      cacheExpiresAt?: string;
      imageId: number;
      retryAfterMs?: number;
      status: 'FAILED' | 'PENDING' | 'READY';
      url?: string;
    }>;
  }>(`${base}/product-management/images/cache`, data);
}

export function syncProductManagementApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/product-management/sync`,
    data,
  );
}

export function getProductManagementSyncStatusApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>[]>(
    `${base}/product-management/sync-status`,
    data,
  );
}

export function searchPromotionsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/promotions/search`, data, {
    timeout: 60_000,
  });
}

export function getPromotionDetailApi(data: {
  activityId: string;
  forceRefresh?: boolean;
  lightweight?: boolean;
  shopId: number;
}) {
  return requestClient.post<ApiPayload>(`${base}/promotions/detail`, data, {
    timeout: 60_000,
  });
}

export function listFlashSaleRenewalsApi(data: { shopId: number }) {
  return requestClient.post<Record<string, any>[]>(
    `${base}/promotions/auto-renew/list`,
    data,
  );
}

export function getPromotionCountsApi(data: {
  activityIds: string[];
  shopId: number;
}) {
  return requestClient.post<ApiPayload>(`${base}/promotions/counts`, data);
}

export function searchPromotionProductsApi(data: {
  keyword: string;
  productIds: string[];
  shopId: number;
}) {
  return requestClient.post<ApiPayload>(
    `${base}/promotions/product-search`,
    data,
  );
}

export function getPromotionProductDisplayApi(data: {
  productIds: string[];
  shopId: number;
}) {
  return requestClient.post<ApiPayload>(
    `${base}/promotions/product-display`,
    data,
    { timeout: 60_000 },
  );
}

export function saveFlashSaleRenewalApi(data: Record<string, any>) {
  return requestClient.post<Record<string, any>>(
    `${base}/promotions/auto-renew/save`,
    data,
  );
}

export function searchCouponsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/coupons/search`, data, {
    timeout: 60_000,
  });
}

export function createPromotionApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/promotions/create`, data);
}

export function updatePromotionApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/promotions/update`, data);
}

export function deactivatePromotionApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(`${base}/promotions/deactivate`, data);
}

export function updatePromotionProductsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(
    `${base}/promotions/products/update`,
    data,
  );
}

export function removePromotionProductsApi(data: Record<string, any>) {
  return requestClient.post<ApiPayload>(
    `${base}/promotions/products/remove`,
    data,
  );
}

export type * from './model';
